import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  listThemes, assignTemplateTheme, assignPageTheme,
  type CmsTheme, type OwnerRef,
} from '../../lib/cmsAdmin';
import { ApiError } from '../../lib/apiClient';

/**
 * Theme: Inherit, or a named theme.
 *
 * ── AN ASSIGNMENT, NOT A SET OF VALUES ───────────────────────────────────────────────
 *
 * §44/§68. The template editor never exposes a colour. It names a theme, or names
 * nothing and inherits one, and the theme is edited in its own place. Putting token
 * values here would mean every template that wanted the brand carried its own copy of
 * it, and rebranding would become a sweep through every template anybody had ever made.
 *
 * ── INHERIT IS THE DEFAULT AND IT SAYS WHAT IT MEANS ─────────────────────────────────
 *
 * "Inherit" is not "no theme". It is "whatever this organization, or the platform,
 * currently uses" — which is why the effective source is shown beside it rather than
 * left for somebody to work out.
 */
export function ThemeControl({
  owner, organizationId, scope, themeId, effectiveSource, onChanged,
}: {
  owner: OwnerRef;
  organizationId: string | null;
  scope: 'platform' | 'organization';
  themeId: string | null;
  /** Which step supplies the theme when this one inherits. */
  effectiveSource?: string | null;
  onChanged: (themeId: string | null) => void;
}) {
  const [themes, setThemes] = useState<CmsTheme[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listThemes({ organizationId })
      .then((rows) => {
        if (cancelled) return;
        // A platform owner renders for every organization, so only platform themes are
        // legal for it. The server refuses the rest; offering them would be a dead end.
        setThemes(scope === 'platform' ? rows.filter((t) => t.scope === 'platform') : rows);
      })
      .catch(() => { if (!cancelled) setThemes([]); });
    return () => { cancelled = true; };
  }, [organizationId, scope]);

  // Global sections inherit the theme of the page they render into (§46), so there is
  // nothing to assign here. Saying so is better than an empty control.
  if (owner.kind === 'global_section') {
    return (
      <p className="cms-bar__theme-note adm-hint">
        Inherits the theme of each page it appears on.
      </p>
    );
  }

  async function assign(next: string | null) {
    setBusy(true);
    setError(null);
    try {
      if (owner.kind === 'template') await assignTemplateTheme(owner.id, next);
      else await assignPageTheme(owner.id, next);
      onChanged(next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That theme could not be assigned.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <label className="cms-bar__theme">
      <span className="field__label">Theme</span>
      <select
        className="adm-select"
        value={themeId ?? ''}
        disabled={busy}
        onChange={(e) => { void assign(e.target.value || null); }}
      >
        <option value="">
          Inherit{effectiveSource ? ` — currently ${describeSource(effectiveSource)}` : ''}
        </option>
        {themes.map((theme) => (
          <option key={theme.id} value={theme.id}>
            {theme.name}
            {theme.isDefault ? ' (default)' : ''}
            {theme.scope === 'platform' ? ' · platform' : ''}
            {theme.publishedVersion ? '' : ' · never published'}
          </option>
        ))}
      </select>
      {error && <span className="cms-slot__missing">{error}</span>}
      {themeId && (
        <Link className="field__help" to={`/admin/content/themes/${themeId}`}>Open theme</Link>
      )}
    </label>
  );
}

/** §33: the resolved step, in words rather than as a code. */
export function describeSource(source: string): string {
  switch (source) {
    case 'page': return 'this page’s own theme';
    case 'template': return 'the template’s theme';
    case 'venue': return 'the venue’s theme';
    case 'organization': return 'the organization default';
    case 'platform': return 'the platform default';
    default: return 'Voleto’s shipped appearance';
  }
}
